import React, { useContext, useEffect, useState } from 'react';
import { gql } from 'apollo-boost';
import { useMutation } from '@apollo/react-hooks';
import { useNavigate } from 'react-router';
import { IdsButton } from '@investec/ids-web-react';
import { customerDataContext } from '../../../../contexts/CustomerDataProvider';
import CloseButton from '../../../SharedComponents/CloseButton3';
import Loading from '../../../Loading/Loading';
import SmsOtp from '../../../Session/SmsOtp';
import Modal from '../../../Modal/Modal';
import Button from '../../../Form/Button/Button';
import Select from '../../../Form/Select/Select';
import logoImg from '../../../../assets/images/investec-logo.svg';
import successImg from '../../../../assets/images/success.png';
import { fisaPenaltyClosureReasons } from '../FISA/Penalty/fisaPenaltyClosureReasons';
import { formatCurrency } from '../../../../utils/helpers';
import IsaTransferInstructionsModal from './IsaTransferInstructionsModal';
import InstantAccessCashIsaWithdrawalConfirmation from './InstantAccessCashIsaWithdrawalConfirmation';
import withFeatureFlags from '../../../FeatureFlags/withFeatureFlags';
import '../FisaAccountClosure.scss';
import './InstantAccessCashIsaClosure.scss';

const CLOSE_ACCOUNT = gql`
    mutation CloseAccount($request: CloseAccountRequestType!) {
        closeAccount(request: $request) {
            success
        }
    }
`;

const ADDITIONAL_REASON_LIMIT = 250;

const InstantAccessCashIsaClosure = ({ featureEnabled }) => {
    const accountIdFromUrl = window.location.pathname.split('/').pop();
    const navigate = useNavigate();
    const customerData = useContext(customerDataContext)?.data;
    const [account, setAccount] = useState(null);
    const [reason, setReason] = useState(null);
    const [additionalReason, setAdditionalReason] = useState('');
    const [decision, setDecision] = useState(null);
    const [completionToken, setCompletionToken] = useState('');
    const [isClosing, setIsClosing] = useState(false);
    const [showReasonError, setShowReasonError] = useState(false);
    const [showAdditionalReasonError, setShowAdditionalReasonError] = useState(false);
    const [showDecisionError, setShowDecisionError] = useState(false);
    const [showClosureError, setShowClosureError] = useState(false);
    const [showWithdrawalConfirmation, setShowWithdrawalConfirmation] = useState(false);
    const [showOtpModal, setShowOtpModal] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [closeAccount] = useMutation(CLOSE_ACCOUNT);

    useEffect(() => {
        if (!customerData?.customer?.accounts) return;

        const foundAccount = customerData.customer.accounts.find(item => item.id === accountIdFromUrl);
        const isDisa = foundAccount?.product?.productType === 'INSTANT_ACCESS_CASH_ISA';

        if (!featureEnabled('EnableFisaMaturity') || !isDisa || foundAccount.accountStatus === 'Closed' || foundAccount.accountStatus === 'ClosureRequested') {
            navigate('/');
            return;
        }

        setAccount(foundAccount);
    }, [accountIdFromUrl, customerData, featureEnabled, navigate]);

    if (!customerData || !account) return <Loading message="Loading..." />;

    const goBack = () => navigate(`/account-details/${account.id}`);
    const requiresAdditionalReason = reason?.value === 'other';
    const hasAdditionalReason = !requiresAdditionalReason || Boolean(additionalReason.trim());

    const continueClosure = () => {
        if (!reason) setShowReasonError(true);
        if (!hasAdditionalReason) setShowAdditionalReasonError(true);
        if (!decision) setShowDecisionError(true);
        if (!reason || !decision || !hasAdditionalReason) return;

        if (decision === 'transfer') {
            setShowTransferModal(true);
            return;
        }

        setShowWithdrawalConfirmation(true);
    };

    const handleOtpSuccess = async token => {
        setCompletionToken(token);
        setIsClosing(true);
        setShowClosureError(false);

        try {
            const result = await closeAccount({ variables: { request: { accountId: account.id, completionToken: token, closureReason: reason?.value || '', additionalClosureReason: requiresAdditionalReason ? additionalReason.trim() : '' } } });
            if (result.data?.closeAccount?.success) {
                setShowOtpModal(false);
                setShowSuccessModal(true);
            } else {
                setShowClosureError(true);
            }
        } catch {
            setShowClosureError(true);
        } finally {
            setIsClosing(false);
        }
    };

    return <>
        <div className="fisa-account-closure">
            <div className="fisa-account-closure__flex-row"><img src={logoImg} alt="Investec" /><CloseButton onClick={goBack} /></div>
            <div className="fisa-account-closure__container text__navy fisa-account-closure fisa-account-closure-form-container disa-closure__shell">
                <h1 className="fisa-account-closure__title fisa-account-closure__span_content2">Close your {account.product?.name}</h1>
                <div className="fisa-account-closure__flex-row fisa-account-closure__flex-row--responsive">
                    <div className="fisa-account-closure__info-box disa-closure__summary" data-di-mask>
                        <div className="disa-closure__summary-offering"><strong>Your current Instant Access Cash ISA</strong></div>
                        <p>Account number: {account.accountNumber}</p><p>Balance: {formatCurrency(account.balance || 0)}</p>
                    </div>
                </div>
                {!showWithdrawalConfirmation ? <section className="disa-closure__content">
                    <h2>We&apos;re sorry to hear you&apos;d like to close your account.</h2>
                    <p>If you close this account, you won&apos;t be able to open another Instant Access Cash ISA. This account is only available to customers when their Fixed Rate Cash ISA matures.</p>
                    <p>You can keep your savings tax free by transferring them into a new Fixed Rate Cash ISA. To get started, log into your account and select ‘Open a new account’.</p>
                    <div className="disa-closure__field">
                        <p className="disa-closure__label">What is the reason for closing your account?</p>
                        <Select
                            options={fisaPenaltyClosureReasons}
                            placeholder="Select a reason"
                            customClassNames="disa-closure__reason-select"
                            hasError={showReasonError}
                            value={reason}
                            onChange={option => {
                                setReason(option);
                                setAdditionalReason('');
                                setShowReasonError(false);
                                setShowAdditionalReasonError(false);
                            }}
                        />
                        {showReasonError && <p className="login__error--wrap">Please select a reason.</p>}
                    </div>
                    {requiresAdditionalReason && <div className="disa-closure__textarea-wrapper">
                        <label htmlFor="disa-additional-closure-reason" className="disa-closure__textarea-label">Could you tell us more?</label>
                        <textarea
                            id="disa-additional-closure-reason"
                            className="disa-closure__textarea"
                            placeholder="Please specify your reason for closing the account"
                            value={additionalReason}
                            rows={4}
                            maxLength={ADDITIONAL_REASON_LIMIT}
                            required
                            aria-invalid={showAdditionalReasonError}
                            aria-describedby={`disa-additional-closure-reason-count${showAdditionalReasonError ? ' disa-additional-closure-reason-error' : ''}`}
                            onChange={event => {
                                setAdditionalReason(event.target.value.slice(0, ADDITIONAL_REASON_LIMIT));
                                setShowAdditionalReasonError(false);
                            }}
                        />
                        <span id="disa-additional-closure-reason-count" className="disa-closure__char-count">{additionalReason.length}/{ADDITIONAL_REASON_LIMIT} characters used</span>
                        {showAdditionalReasonError && <p id="disa-additional-closure-reason-error" className="disa-closure__validation-message">Please add a few more details.</p>}
                    </div>}
                    {reason && <fieldset className="disa-closure__decision">
                        <legend>What would you like to do with your tax-free ISA savings?</legend>
                        <label className={`disa-closure__choice ${decision === 'withdraw' ? 'disa-closure__choice--selected' : ''}`}><input type="radio" name="disa-decision" value="withdraw" checked={decision === 'withdraw'} onChange={() => { setDecision('withdraw'); setShowDecisionError(false); }} /><span><strong>To my linked current account</strong><small>We&apos;ll close your account and return your full balance to your linked account. You&apos;ll lose the ISA tax-free status on the entire amount you withdraw.</small></span></label>
                        <label className={`disa-closure__choice ${decision === 'transfer' ? 'disa-closure__choice--selected' : ''}`}><input type="radio" name="disa-decision" value="transfer" checked={decision === 'transfer'} onChange={() => { setDecision('transfer'); setShowDecisionError(false); }} /><span><strong>Transfer out to another ISA provider</strong><small>We&apos;ll transfer your full balance, including any interest you&apos;ve earned but haven&apos;t yet received, to your new ISA provider.</small></span></label>
                        {showDecisionError && <p className="login__error--wrap">Please select how you would like to proceed.</p>}
                    </fieldset>}
                    {reason && <div className="disa-closure__actions"><IdsButton appearance="subtle" size="md" onClick={goBack}>Cancel</IdsButton><IdsButton appearance="primary" size="md" onClick={continueClosure} disabled={!decision || !hasAdditionalReason}>Continue</IdsButton></div>}
                </section> : <InstantAccessCashIsaWithdrawalConfirmation
                    account={account}
                    onConfirm={() => setShowOtpModal(true)}
                    onCancel={() => setShowWithdrawalConfirmation(false)}
                />}
            </div>
        </div>

        {showOtpModal && <Modal className="modal modal--fixed-height modal--wide inactivity-modal modal__mobile-optimised" onClose={() => setShowOtpModal(false)} mobileOptimised><h2 className="modal__title">Verify account closure</h2>{showClosureError && <p className="login__error--wrap">Something went wrong. Please try again.</p>}<SmsOtp mobileNumber={customerData.customer.mobileNumber || ''} onSuccess={(_event, _mobileNumber, token) => handleOtpSuccess(token || completionToken)} analyticsRegion="account details" analyticsAction="account closure" />{isClosing && <p className="modal__slim">Closing your account...</p>}</Modal>}

        {showSuccessModal && <Modal className="modal modal--fixed-height modal--wide inactivity-modal modal__mobile-optimised" onClose={() => navigate('/')} mobileOptimised><div className="disa-closure__success-modal"><img src={successImg} className="account-activity__image" alt="" /><h2>Thank you</h2><p>Your account is scheduled to close.</p><Button size="med" customClassNames={["modal__button"]} label="OK" onClick={() => navigate('/')} /></div></Modal>}

        <IsaTransferInstructionsModal isOpen={showTransferModal} onClose={() => setShowTransferModal(false)} onReturnToDashboard={() => navigate('/')} isDisaTransfer />
    </>;
};

export default withFeatureFlags(InstantAccessCashIsaClosure);
